import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BundleFlightData } from '../FlightDetails/flightSlice';
import { OptionalFilter } from '../../components/SideFilters/interface';
import { SupplierError, AsyncSearchStatus } from './flightAsyncSlice';

export interface FlightDynamicAsyncState {
  // Outbound flight state
  outbound: {
    status: AsyncSearchStatus;
    flights: BundleFlightData[];
    returnedSuppliers: number;
    supplierErrors: SupplierError[];
    optionalFilters: OptionalFilter[];
    error: string | null;
    searchStartTime: number | null;
    searchEndTime: number | null;
    totalCount: number;
    successCount: number;
    errorCount: number;
  };
  // Return flight state
  return: {
    status: AsyncSearchStatus;
    flights: BundleFlightData[];
    returnedSuppliers: number;
    supplierErrors: SupplierError[];
    optionalFilters: OptionalFilter[];
    error: string | null;
    searchStartTime: number | null;
    searchEndTime: number | null;
    totalCount: number;
    successCount: number;
    errorCount: number;
  };
}

const initialDirectionState = {
  status: 'idle' as AsyncSearchStatus,
  flights: [],
  returnedSuppliers: 0,
  supplierErrors: [],
  optionalFilters: [],
  error: null,
  searchStartTime: null,
  searchEndTime: null,
  totalCount: 0,
  successCount: 0,
  errorCount: 0,
};

const initialState: FlightDynamicAsyncState = {
  outbound: { ...initialDirectionState },
  return: { ...initialDirectionState },
};

export const flightDynamicAsyncSlice = createSlice({
  name: 'flightDynamicAsync',
  initialState,
  reducers: {
    // Outbound flight actions
    startSSESearchOutbound: (state) => {
      state.outbound = {
        ...initialDirectionState,
        status: 'loading_all',
        searchStartTime: Date.now(),
      };
    },

    receiveFirstChunkOutbound: (
      state,
      action: PayloadAction<{
        flights: BundleFlightData[];
        ts_id: number;
        ts_type?: string;
      }>
    ) => {
      state.outbound.status = 'first_chunk_received';
      state.outbound.flights.push(...action.payload.flights);
      state.outbound.returnedSuppliers += 1;
    },

    receiveChunkOutbound: (
      state,
      action: PayloadAction<{
        flights: BundleFlightData[];
        ts_id: number;
        ts_type?: string;
      }>
    ) => {
      if (
        state.outbound.status === 'first_chunk_received' ||
        state.outbound.status === 'loading_all'
      ) {
        state.outbound.status = 'streaming';
      }
      state.outbound.flights.push(...action.payload.flights);
      state.outbound.returnedSuppliers += 1;
    },

    receiveSupplierErrorOutbound: (state, action: PayloadAction<SupplierError>) => {
      state.outbound.supplierErrors.push(action.payload);
      state.outbound.returnedSuppliers += 1;
    },

    updateOptionalFiltersOutbound: (state, action: PayloadAction<OptionalFilter[]>) => {
      state.outbound.optionalFilters = action.payload;
    },

    updateProgressOutbound: (
      state,
      action: PayloadAction<{
        totalCount: number;
        successCount: number;
        errorCount: number;
      }>
    ) => {
      state.outbound.totalCount = action.payload.totalCount;
      state.outbound.successCount = action.payload.successCount;
      state.outbound.errorCount = action.payload.errorCount;
    },

    completeSSESearchOutbound: (state) => {
      state.outbound.status = 'complete';
      state.outbound.searchEndTime = Date.now();
    },

    errorSSESearchOutbound: (state, action: PayloadAction<string>) => {
      state.outbound.status = 'error';
      state.outbound.error = action.payload;
      state.outbound.searchEndTime = Date.now();
    },

    // Return flight actions
    startSSESearchReturn: (state) => {
      state.return = {
        ...initialDirectionState,
        status: 'loading_all',
        searchStartTime: Date.now(),
      };
    },

    receiveFirstChunkReturn: (
      state,
      action: PayloadAction<{
        flights: BundleFlightData[];
        ts_id: number;
        ts_type?: string;
      }>
    ) => {
      state.return.status = 'first_chunk_received';
      state.return.flights.push(...action.payload.flights);
      state.return.returnedSuppliers += 1;
    },

    receiveChunkReturn: (
      state,
      action: PayloadAction<{
        flights: BundleFlightData[];
        ts_id: number;
        ts_type?: string;
      }>
    ) => {
      if (
        state.return.status === 'first_chunk_received' ||
        state.return.status === 'loading_all'
      ) {
        state.return.status = 'streaming';
      }
      state.return.flights.push(...action.payload.flights);
      state.return.returnedSuppliers += 1;
    },

    receiveSupplierErrorReturn: (state, action: PayloadAction<SupplierError>) => {
      state.return.supplierErrors.push(action.payload);
      state.return.returnedSuppliers += 1;
    },

    updateOptionalFiltersReturn: (state, action: PayloadAction<OptionalFilter[]>) => {
      state.return.optionalFilters = action.payload;
    },

    updateProgressReturn: (
      state,
      action: PayloadAction<{
        totalCount: number;
        successCount: number;
        errorCount: number;
      }>
    ) => {
      state.return.totalCount = action.payload.totalCount;
      state.return.successCount = action.payload.successCount;
      state.return.errorCount = action.payload.errorCount;
    },

    completeSSESearchReturn: (state) => {
      state.return.status = 'complete';
      state.return.searchEndTime = Date.now();
    },

    errorSSESearchReturn: (state, action: PayloadAction<string>) => {
      state.return.status = 'error';
      state.return.error = action.payload;
      state.return.searchEndTime = Date.now();
    },

    // Reset both directions
    resetDynamicAsyncSearch: (state) => {
      return initialState;
    },
  },
});

export const {
  startSSESearchOutbound,
  receiveFirstChunkOutbound,
  receiveChunkOutbound,
  receiveSupplierErrorOutbound,
  updateOptionalFiltersOutbound,
  updateProgressOutbound,
  completeSSESearchOutbound,
  errorSSESearchOutbound,
  startSSESearchReturn,
  receiveFirstChunkReturn,
  receiveChunkReturn,
  receiveSupplierErrorReturn,
  updateOptionalFiltersReturn,
  updateProgressReturn,
  completeSSESearchReturn,
  errorSSESearchReturn,
  resetDynamicAsyncSearch,
} = flightDynamicAsyncSlice.actions;

// Selectors for outbound
export const selectOutboundStatus = (state: {
  flightDynamicAsync: FlightDynamicAsyncState;
}) => state.flightDynamicAsync.outbound.status;

export const selectOutboundFlights = (state: {
  flightDynamicAsync: FlightDynamicAsyncState;
}) => state.flightDynamicAsync.outbound.flights;

export const selectOutboundOptionalFilters = (state: {
  flightDynamicAsync: FlightDynamicAsyncState;
}) => state.flightDynamicAsync.outbound.optionalFilters;

export const selectOutboundProgress = (state: {
  flightDynamicAsync: FlightDynamicAsyncState;
}) => {
  const { totalCount, successCount, errorCount } =
    state.flightDynamicAsync.outbound;
  const completed = successCount + errorCount;
  const percentage = totalCount > 0 ? (completed / totalCount) * 100 : 0;
  return { totalCount, successCount, errorCount, completed, percentage };
};

// Selectors for return
export const selectReturnStatus = (state: {
  flightDynamicAsync: FlightDynamicAsyncState;
}) => state.flightDynamicAsync.return.status;

export const selectReturnFlights = (state: {
  flightDynamicAsync: FlightDynamicAsyncState;
}) => state.flightDynamicAsync.return.flights;

export const selectReturnOptionalFilters = (state: {
  flightDynamicAsync: FlightDynamicAsyncState;
}) => state.flightDynamicAsync.return.optionalFilters;

export const selectReturnProgress = (state: {
  flightDynamicAsync: FlightDynamicAsyncState;
}) => {
  const { totalCount, successCount, errorCount } =
    state.flightDynamicAsync.return;
  const completed = successCount + errorCount;
  const percentage = totalCount > 0 ? (completed / totalCount) * 100 : 0;
  return { totalCount, successCount, errorCount, completed, percentage };
};

export default flightDynamicAsyncSlice.reducer;
